import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import GavelIcon from "@mui/icons-material/Gavel";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import fetchWithAuth from "../../../util/fetchWithAuth";
import { API_URL } from "../../../util/url";
import { useParams, useNavigate } from "react-router-dom";

export default function ProfileBanner({ onDeleted, redirectPath }) {
  const [card, setCard] = useState("");
  const [cardModalOpen, setCardModalOpen] = useState(false);
  const [bonusFineModalOpen, setBonusFineModalOpen] = useState(false);
  const [bonusFineType, setBonusFineType] = useState("bonus");
  const [bonusFineAmount, setBonusFineAmount] = useState("");
  const [bonusFineComment, setBonusFineComment] = useState("");
  const [bonusFineLoading, setBonusFineLoading] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const { operatorId } = useParams();
  const navigate = useNavigate();

  const getErrorMessage = async (response) => {
    try {
      const data = await response.json();
      return (
        data?.detail ||
        data?.message ||
        (typeof data === "string" ? data : "Помилка запиту")
      );
    } catch {
      return "Помилка запиту";
    }
  };

  const loadOperatorCard = async () => {
    try {
      const response = await fetchWithAuth(`${API_URL}/user/operators/${operatorId}/`);
      if (!response.ok) return;
      const data = await response.json();
      setCard(data?.card_number || "");
    } catch (err) {
      console.error("Помилка завантаження картки:", err);
    }
  };

  useEffect(() => {
    loadOperatorCard();
  }, [operatorId]);

  const handleCardOpen = () => setCardModalOpen(true);
  const handleCardClose = () => setCardModalOpen(false);
  const handleDeleteClick = () => setDeleteConfirmOpen(true);
  const handleDeleteClose = () => setDeleteConfirmOpen(false);

  const handleSaveCard = async () => {
    setLoading(true);
    try {
      const response = await fetchWithAuth(`${API_URL}/user/operators/${operatorId}/`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ card_number: card }),
      });

      if (!response.ok) {
        throw new Error(await getErrorMessage(response));
      }

      setCardModalOpen(false);
    } catch (err) {
      console.error("Помилка збереження картки:", err);
      alert(err?.message || "Не вдалося зберегти картку");
    } finally {
      setLoading(false);
    }
  };

  const openBonusFineModal = (type) => {
    setBonusFineType(type);
    setBonusFineAmount("");
    setBonusFineComment("");
    setBonusFineModalOpen(true);
  };

  const closeBonusFineModal = () => {
    if (bonusFineLoading) return;
    setBonusFineModalOpen(false);
  };

  const handleSubmitBonusFine = async () => {
    const parsed = Number(String(bonusFineAmount).replace(",", "."));
    if (!Number.isFinite(parsed) || parsed <= 0) {
      alert("Вкажіть коректну суму");
      return;
    }

    setBonusFineLoading(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const signed = bonusFineType === "fine" ? -Math.abs(parsed) : Math.abs(parsed);
      const response = await fetchWithAuth(`${API_URL}/finance/payments/`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          user_id: Number(operatorId),
          date: today,
          amount: 0,
          addsum: signed,
          comment: bonusFineComment || "",
        }),
      });

      if (!response.ok) {
        throw new Error(await getErrorMessage(response));
      }

      setBonusFineModalOpen(false);
    } catch (err) {
      console.error("Помилка додавання бонусу/штрафу:", err);
      alert(err?.message || "Не вдалося виконати операцію");
    } finally {
      setBonusFineLoading(false);
    }
  };

  const handleConfirmDelete = async () => {
    setLoading(true);
    try {
      const response = await fetchWithAuth(
        `${API_URL}/user/operators/${operatorId}/`,
        {
          method: "DELETE",
        }
      );

      if (!response.ok) {
        throw new Error(await getErrorMessage(response));
      }

      setDeleteConfirmOpen(false);
      onDeleted?.();

      if (redirectPath) {
        navigate(redirectPath);
      }
    } catch (err) {
      console.error("Помилка видалення:", err);
      alert(err?.message || "Не вдалося видалити оператора");
    } finally {
      setLoading(false);
    }
  };
  return (
    <>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 12px",
          borderRadius: "8px",
          gap: 2,
        }}
      >
        <Button
          variant="outlined"
          endIcon={<DeleteIcon />}
          sx={{
            color: "red !important",
            borderColor: "#172036",
            p: 2,
            borderRadius: "10px",
            fontWeight: "bold",
            textTransform: "none",
            "&:hover": { backgroundColor: "rgba(255,0,0,0.1)" },
          }}
          onClick={handleDeleteClick}
        >
          Вид.
        </Button>

        <Button
          variant="outlined"
          endIcon={<GavelIcon />}
          sx={{
            color: "red !important",
            borderColor: "#172036",
            p: 2,
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: "bold",
            "&:hover": { backgroundColor: "rgba(255,0,0,0.1)" },
          }}
          onClick={() => openBonusFineModal("fine")}
        >
          Штраф
        </Button>

        <Button
          variant="contained"
          endIcon={<CardGiftcardIcon sx={{ color: "#4CAF50" }} />}
          className="rmui-card"
          onClick={() => openBonusFineModal("bonus")}
          sx={{
            textTransform: "none",
            px: 3,
            backgroundColor: "#0D6EFD",
            color: "green !important",
            py: 2,
            borderRadius: "10px",
            fontWeight: "bold",
          }}
        >
          Бонус
        </Button>

        <Button
          variant="contained"
          startIcon={<CreditCardIcon sx={{ color: "#605DFF !important" }} />}
          className="rmui-card"
          onClick={handleCardOpen}
          sx={{
            backgroundColor: "#0D6EFD",
            textTransform: "none",
            px: 3,
            py: 2,
            borderRadius: "10px",
            fontWeight: "bold",
            "&:hover": { backgroundColor: "#0B5ED7" },
          }}
        >
          <Typography variant="subtitle2"> Карта людини</Typography>
        </Button>
      </Box>

      {/* Модалка для карты */}
      <Dialog
        open={cardModalOpen}
        onClose={handleCardClose}
        fullWidth
        maxWidth="sm"
        PaperProps={{ sx: { minHeight: "210px" } }}
      >
        <DialogContent
          className="rmui-card"
          sx={{ p: 2, borderRadius: "15px" }}
        >
          <DialogTitle sx={{ p: 0, mb: 2 }}>Реквізити людини</DialogTitle>
          <Typography>Банківська карта</Typography>
          <TextField
            autoFocus
            fullWidth
            value={card}
            size="small"
            sx={{ mt: 1 }}
            onChange={(e) => setCard(e.target.value)}
          />
          <DialogActions sx={{ justifyContent: "start", mt: 2 }}>
            <Button
              sx={{ borderRadius: "15px", textTransform: "none" }}
              onClick={handleSaveCard}
              variant="contained"
            >
              <Typography variant="subtitle2">Змінити</Typography>
            </Button>
            <Button
              onClick={handleCardClose}
              color="inherit"
              sx={{ borderRadius: "15px", textTransform: "none" }}
            >
              Закрити
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>

      {/* Подтверждение удаления */}
      <Dialog open={deleteConfirmOpen} onClose={handleDeleteClose}>
      <DialogTitle>Підтвердження видалення</DialogTitle>
      <DialogContent>
        <Typography>Ви точно хочете видалити цього оператора?</Typography>
      </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteClose} disabled={loading}>
            Ні
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={loading}
          >
            Так
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={bonusFineModalOpen}
        onClose={closeBonusFineModal}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>
          {bonusFineType === "fine" ? "Додати штраф" : "Додати бонус"}
        </DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            label="Тип"
            value={bonusFineType}
            onChange={(e) => setBonusFineType(e.target.value)}
            sx={{ mt: 1 }}
            disabled={bonusFineLoading}
          >
            <MenuItem value="bonus">Бонус</MenuItem>
            <MenuItem value="fine">Штраф</MenuItem>
          </TextField>
          <TextField
            fullWidth
            label="Сума"
            type="number"
            value={bonusFineAmount}
            onChange={(e) => setBonusFineAmount(e.target.value)}
            sx={{ mt: 2 }}
            disabled={bonusFineLoading}
          />
          <TextField
            fullWidth
            label="Коментар"
            value={bonusFineComment}
            onChange={(e) => setBonusFineComment(e.target.value)}
            sx={{ mt: 2 }}
            disabled={bonusFineLoading}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeBonusFineModal} disabled={bonusFineLoading}>
            Закрити
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmitBonusFine}
            disabled={bonusFineLoading}
          >
            {bonusFineLoading ? "Збереження..." : "Зберегти"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
